/**
 * No Relief — which counters go unmanned, and who has been told.
 *
 * A counter has "no relief" at the slot where it goes from manned to unmanned:
 * the officer there finishes and nobody takes over. AC2 manned 1000-1500,
 * 1600-1700, 1700-1830 and 2100-2200 has no relief at 1500 and at 1830 — the
 * handover at 1700 is continuous, and 2200 is the end of the shift.
 *
 * Derived from the roster itself, so it follows every edit instantly. The call
 * log (acks) is stored per session; a tick records a call someone actually
 * made and survives any roster edit — only pressing the same tick again (an
 * explicit undo) removes it.
 */

import type { NoReliefAck, NoReliefAckKind } from '@/types/api';

export interface NoReliefPoint {
  /** 0-based counter_matrix row. */
  counter: number;
  /** 0-based slot at which the counter goes unmanned. */
  slot: number;
}

/** Every manned → unmanned transition inside the shift, per counter. */
export function computeNoRelief(counterMatrix: string[][][]): NoReliefPoint[] {
  const points: NoReliefPoint[] = [];
  counterMatrix.forEach((slots, counter) => {
    for (let slot = 1; slot < slots.length; slot++) {
      const wasManned = (slots[slot - 1]?.length ?? 0) > 0;
      const isManned = (slots[slot]?.length ?? 0) > 0;
      if (wasManned && !isManned) points.push({ counter, slot });
    }
  });
  return points;
}

export interface NoReliefEntry extends NoReliefPoint {
  /** Whether the roster, as it stands now, still has no relief here. */
  current: boolean;
  /** Calls already made for this point, oldest first. */
  acks: NoReliefAck[];
  /**
   * The call still owed, if any: 'no_relief' when nobody has told the officer
   * they are not being relieved; 'relief' when they were told that, and an edit
   * has since put someone on the counter after all.
   */
  pending: NoReliefAckKind | null;
}

export interface NoReliefGroup {
  slot: number;
  entries: NoReliefEntry[];
}

const keyOf = (p: NoReliefPoint) => `${p.counter}:${p.slot}`;

/**
 * What buildNoReliefGroups remembers between calls, per (counter, slot) —
 * only for points that have at least one ack, since a point with none is
 * fully described by "is it current right now" alone (see below).
 */
interface NoReliefTrackingEntry {
  /** The live kind ('no_relief' if currently in `points`, else 'relief') as
   *  of the last call this key was seen in. */
  liveKind: NoReliefAckKind;
  /** ack count as of the last call, to notice a tick (or an undo) happening. */
  ackCount: number;
  /**
   * True when the live kind has changed since the latest ack WITHOUT a new
   * ack covering that change — i.e. there's a call owed for whatever the
   * live kind is now, even if it happens to match the latest ack's kind
   * (a no_relief → relief → no_relief round trip nobody called about in
   * between still owes a fresh no_relief call, not "already handled").
   */
  dirty: boolean;
}
export type NoReliefTrackingState = Map<string, NoReliefTrackingEntry>;

/**
 * Merge the live no-relief points with the call log, grouped by time —
 * threading `prevState` through so a live transition this hook has actually
 * witnessed (a round trip through the opposite kind with no tick in between)
 * still shows as owed, rather than looking resolved purely because the kind
 * it lands back on happens to match the latest tick.
 *
 * Call with `prevState` as an empty Map for a one-shot check (fine as long as
 * there's no round trip to catch — most callers, including every existing
 * single-snapshot test, are this simple). A caller that re-renders on every
 * live update (see useNoRelief) should keep the returned `state` and pass it
 * back in next time.
 *
 * A point nobody has called about drops out as soon as it gets relief — there
 * is nothing to tell anyone. A point with calls against it stays forever, so
 * the log remains readable.
 */
export function buildNoReliefGroups(
  points: NoReliefPoint[],
  acks: NoReliefAck[],
  prevState: NoReliefTrackingState = new Map(),
): { groups: NoReliefGroup[]; state: NoReliefTrackingState } {
  const currentKeys = new Set(points.map(keyOf));
  const acksByKey = new Map<string, NoReliefAck[]>();
  for (const ack of [...acks].sort((a, b) => a.id - b.id)) {
    const key = keyOf(ack);
    const list = acksByKey.get(key) ?? [];
    list.push(ack);
    acksByKey.set(key, list);
  }

  const allKeys = new Set<string>([...currentKeys, ...acksByKey.keys()]);
  const nextState: NoReliefTrackingState = new Map();
  const bySlot = new Map<number, NoReliefEntry[]>();

  for (const key of allKeys) {
    const [counterStr, slotStr] = key.split(':');
    const counter = Number(counterStr);
    const slot = Number(slotStr);
    const current = currentKeys.has(key);
    const acksForKey = acksByKey.get(key) ?? [];
    const liveKind: NoReliefAckKind = current ? 'no_relief' : 'relief';

    let pending: NoReliefAckKind | null;
    if (acksForKey.length === 0) {
      // Nothing ever ticked here — the plain, common case: owed exactly when
      // it's currently no-relief, nothing to track between calls.
      pending = current ? 'no_relief' : null;
    } else {
      const lastAckKind = acksForKey[acksForKey.length - 1].kind;
      const natural = liveKind !== lastAckKind ? liveKind : null;
      const prev = prevState.get(key);
      let dirty: boolean;
      if (!prev) {
        // First time this call has seen the key — nothing witnessed yet, so
        // trust the direct comparison alone (best effort; a transition that
        // happened entirely before this tab opened can't be recovered).
        dirty = false;
      } else if (acksForKey.length > prev.ackCount) {
        dirty = false; // a fresh tick landed since we last looked — resolved
      } else if (acksForKey.length < prev.ackCount) {
        dirty = natural != null; // an undo happened — fall back to the direct read
      } else if (liveKind !== prev.liveKind) {
        dirty = true; // the roster changed this key's status with no new tick
      } else {
        dirty = prev.dirty;
      }
      pending = natural ?? (dirty ? liveKind : null);
      nextState.set(key, { liveKind, ackCount: acksForKey.length, dirty });
    }

    const entry: NoReliefEntry = { counter, slot, current, acks: acksForKey, pending };
    const list = bySlot.get(slot) ?? [];
    list.push(entry);
    bySlot.set(slot, list);
  }

  const groups = [...bySlot.entries()]
    .sort(([a], [b]) => a - b)
    .map(([slot, list]) => ({ slot, entries: list.sort((a, b) => a.counter - b.counter) }));

  return { groups, state: nextState };
}

/** How many calls are still owed — drives the button's badge. */
export function countPendingCalls(groups: NoReliefGroup[]): number {
  return groups.reduce((n, g) => n + g.entries.filter((e) => e.pending).length, 0);
}

/**
 * What a block's NR button shows, for the block that ends where a counter goes unmanned:
 *   owed    no relief, and nobody has told the officer — red, the one to act on
 *   told    no relief, and the officer was told — white, quiet but still tappable (to undo)
 *   relief  the officer was told there was no relief, and an edit has since put someone on after all —
 *           they are owed a "relief is coming" call
 * A counter that has relief and was never called about has no button: there is nothing to say.
 */
export type NoReliefMarkState = 'owed' | 'told' | 'relief';

export interface NoReliefMark {
  entry: NoReliefEntry;
  state: NoReliefMarkState;
  /** Who is on the counter in the slot before it goes unmanned — the block the button sits on. */
  officers: string[];
}

export function markStateOf(entry: NoReliefEntry): NoReliefMarkState | null {
  if (entry.current) return entry.pending === 'no_relief' ? 'owed' : 'told';
  return entry.pending === 'relief' ? 'relief' : null;
}

/** Every NR button the grids draw, from the same groups the No Relief pop-up lists (so the two never disagree). */
export function noReliefMarks(groups: NoReliefGroup[], counterMatrix: string[][][] | undefined): NoReliefMark[] {
  const marks: NoReliefMark[] = [];
  for (const g of groups) {
    for (const entry of g.entries) {
      const state = markStateOf(entry);
      if (!state) continue;
      marks.push({ entry, state, officers: counterMatrix?.[entry.counter]?.[entry.slot - 1] ?? [] });
    }
  }
  return marks;
}

/** What tapping a mark does — the same thing ticking (or unticking) its pill in the pop-up does. */
export type NoReliefAction =
  | { kind: 'ack'; ackKind: NoReliefAckKind }
  | { kind: 'undo'; ackId: number }
  | null;

export function markAction(mark: NoReliefMark): NoReliefAction {
  if (mark.state === 'owed') return { kind: 'ack', ackKind: 'no_relief' };
  if (mark.state === 'relief') return { kind: 'ack', ackKind: 'relief' };
  const latest = mark.entry.acks[mark.entry.acks.length - 1];
  return latest ? { kind: 'undo', ackId: latest.id } : null;
}

/** Whether the counter (0-based row) goes unmanned at `endSlot`, the end of a block on it — the schedule text's
 *  "(no relief)". Keyed for lookups by `${counter}:${slot}`. */
export function noReliefEnds(groups: NoReliefGroup[]): Set<string> {
  return new Set(groups.flatMap((g) => g.entries.filter((e) => e.current).map((e) => keyOf(e))));
}
export const noReliefKey = (counter: number, slot: number) => keyOf({ counter, slot });
